import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'
import React from 'react'
import styled, { css } from 'styled-components'
import {
  type RadioGroupVariant,
  RadioGroupVariantContext,
} from 'sutra/components/Radio/radio-group-variant'
import { brand, gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'

export interface RadioGroupProps<T extends string | number> {
  // The selected value; `null` means nothing is selected. Omit it (and use
  // `defaultValue`) for an uncontrolled group.
  value?: T | null
  defaultValue?: T | null
  onValueChange?: (value: T) => void
  disabled?: boolean
  // 'button' renders each `Radio` as a segment of one joined control.
  variant?: RadioGroupVariant
  // Layout of the default variant; a button group is always a single row.
  orientation?: 'horizontal' | 'vertical'
  'aria-label'?: string
  'aria-labelledby'?: string
  'data-testid'?: string
  // Lands on the group element, so `styled(RadioGroup)` can size or place it.
  className?: string
  children: React.ReactNode
}

const defaultLayout = css<{ $orientation: 'horizontal' | 'vertical' }>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${({ $orientation }) =>
    $orientation === 'vertical' ? 'column' : 'row'};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? 8 : 16)}px;
`

const buttonLayout = css`
  display: inline-flex;
  box-sizing: border-box;
  border: 1px solid ${gray[300]};
  border-radius: ${radii.sm}px;
  background: ${brand.white};
  overflow: hidden;
`

const Group = styled(BaseRadioGroup)<{
  $variant: RadioGroupVariant
  $orientation: 'horizontal' | 'vertical'
}>`
  ${({ $variant }) => ($variant === 'button' ? buttonLayout : defaultLayout)}
`

export const RadioGroup = <T extends string | number>({
  value,
  defaultValue,
  onValueChange,
  disabled,
  variant = 'default',
  orientation = 'horizontal',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': testId,
  className,
  children,
}: RadioGroupProps<T>): React.ReactElement => (
  <RadioGroupVariantContext.Provider value={variant}>
    <Group
      $variant={variant}
      $orientation={orientation}
      value={value}
      defaultValue={defaultValue}
      // `styled()` erases Base UI's generic, so `next` arrives as `unknown`.
      // The only values a child `Radio` can report are the `T`s the caller
      // wrote on it, and never `null`.
      onValueChange={(next: unknown) => {
        if (next !== null) onValueChange?.(next as T)
      }}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-testid={testId}
      className={className}
    >
      {children}
    </Group>
  </RadioGroupVariantContext.Provider>
)

RadioGroup.displayName = 'RadioGroup'
